import { useEffect, useMemo, type JSX } from 'react'
import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  MathUtils,
  MeshLambertMaterial,
  Object3D,
} from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import { METAL_MATERIAL, WOOD_MATERIAL_LIGHT } from '@utils'

const PISTON_MATERIAL = new MeshLambertMaterial({ color: 'brown' })

// Helper: bake world matrix of a dummy into a cloned geometry.
// Pushes both the source and the clone into `temp` for bulk disposal after merging.
function bakeGeo(geo: BoxGeometry | CylinderGeometry, dummy: Object3D, temp: BufferGeometry[]) {
  dummy.updateWorldMatrix(true, false)
  const baked = geo.clone().applyMatrix4(dummy.matrixWorld)
  temp.push(geo, baked)
  return baked
}

const poleBigRotation = MathUtils.degToRad(35)
const poleSmallRotation = poleBigRotation + MathUtils.degToRad(120)

function buildGeometries() {
  const temp: BufferGeometry[] = []
  // mirrors the JSX hierarchy: outer group → scale 0.1, rot-y 180°, pos [0,0,-0.3]
  const outer = new Object3D()
  outer.position.set(0, 0, -0.3)
  outer.rotation.set(0, Math.PI, 0)
  outer.scale.setScalar(0.1)

  // big poles (mirrored on X)
  const poleBig = new Object3D()
  poleBig.position.set(-1.5, 1.6, -1.9)
  poleBig.scale.set(0.6, 4, 0.4)
  poleBig.rotation.set(poleBigRotation, 0, 0)
  outer.add(poleBig)

  const poleBigR = new Object3D()
  poleBigR.position.set(1.5, 1.6, -1.9)
  poleBigR.scale.set(0.6, 4, 0.4)
  poleBigR.rotation.set(poleBigRotation, 0, 0)
  outer.add(poleBigR)

  // small poles (mirrored on X)
  const poleSmall = new Object3D()
  poleSmall.position.set(-1.5, 1.2, -0.9)
  poleSmall.scale.set(0.6, 2.5, 0.6)
  poleSmall.rotation.set(poleSmallRotation, 0, 0)
  outer.add(poleSmall)

  const poleSmallR = new Object3D()
  poleSmallR.position.set(1.5, 1.2, -0.9)
  poleSmallR.scale.set(0.6, 2.5, 0.6)
  poleSmallR.rotation.set(poleSmallRotation, 0, 0)
  outer.add(poleSmallR)

  // beam group
  const beamGroup = new Object3D()
  beamGroup.position.set(0, 3, -0.65)
  outer.add(beamGroup)

  // wood beam
  const beam = new Object3D()
  beamGroup.add(beam)

  // pistons
  function makePiston(px: number) {
    const root = new Object3D()
    root.position.set(px, 0, 0.2)
    beamGroup.add(root)

    // metal face
    const face = new Object3D()
    root.add(face)

    // brown stem + disc share z-offset group at -0.15
    const stemGroup = new Object3D()
    stemGroup.position.set(0, 0, -0.15)
    root.add(stemGroup)

    const stem = new Object3D()
    stem.position.set(0, 0, 0.5)
    stem.rotation.set(Math.PI / 2, 0, 0)
    stemGroup.add(stem)

    const disc = new Object3D()
    disc.position.set(0, 0, 0.75)
    disc.rotation.set(Math.PI / 2, 0, 0)
    stemGroup.add(disc)

    return { face, stem, disc }
  }

  outer.updateWorldMatrix(false, true)

  const pistonL = makePiston(-1.2)
  const pistonR = makePiston(1.2)

  outer.updateWorldMatrix(false, true)

  const metal = mergeGeometries([
    bakeGeo(new BoxGeometry(), poleBig, temp),
    bakeGeo(new BoxGeometry(), poleBigR, temp),
    bakeGeo(new BoxGeometry(), poleSmall, temp),
    bakeGeo(new BoxGeometry(), poleSmallR, temp),
    bakeGeo(new BoxGeometry(0.8, 0.8, 0.3), pistonL.face, temp),
    bakeGeo(new BoxGeometry(0.8, 0.8, 0.3), pistonR.face, temp),
  ])
  const wood = mergeGeometries([bakeGeo(new BoxGeometry(5.5, 1.2, 0.5), beam, temp)])
  const piston = mergeGeometries([
    bakeGeo(new CylinderGeometry(0.15, 0.15, 0.5, 16), pistonL.stem, temp),
    bakeGeo(new CylinderGeometry(0.6, 0.6, 0.2, 32), pistonL.disc, temp),
    bakeGeo(new CylinderGeometry(0.15, 0.15, 0.5, 16), pistonR.stem, temp),
    bakeGeo(new CylinderGeometry(0.6, 0.6, 0.2, 32), pistonR.disc, temp),
  ])
  temp.forEach(g => g.dispose())

  return { metal, wood, piston }
}

export function BufferStopModel(props: JSX.IntrinsicElements['group']) {
  const { metal, wood, piston } = useMemo(() => buildGeometries(), [])

  useEffect(() => {
    return () => {
      metal?.dispose()
      wood?.dispose()
      piston?.dispose()
    }
  }, [metal, wood, piston])

  return (
    <group {...props}>
      <mesh geometry={metal} material={METAL_MATERIAL} castShadow receiveShadow />
      <mesh geometry={wood} material={WOOD_MATERIAL_LIGHT} castShadow receiveShadow />
      <mesh geometry={piston} material={PISTON_MATERIAL} />
    </group>
  )
}
